import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import ClickOutHandler from 'react-onclickout';
import Datetime from 'react-datetime';

const Hour = styled.td`
  text-align: center;
  ${(props) => (props.wide ? 'width: 25%;' : '')}
  width: 16%;
`;

const HourPickerItem = (props: {
  wide?: boolean;
  onClick: Function;
  className: string;
  title: number | string;
}) => (
  <Hour wide={props.wide}>
    <button
      onClick={() => props.onClick()}
      className={props.className}
      type="button"
      style={{ padding: '0px', width: props.wide ? '35px' : '25px' }}
    >
      {props.title}
    </button>
  </Hour>
);

enum DatePickerState {
  Date,
  Time,
  Closed,
}

class DateAdapter extends React.Component<
  { input: any; onChanged?: Function },
  {
    show: DatePickerState;
    current: any;
    temp: any;
    disabled: boolean;
  }
> {
  constructor(props) {
    super(props);

    this.state = {
      show: DatePickerState.Closed,
      current: moment(props.input.value),
      temp: null,
      disabled: props.disabled ?? false,
    };
  }

  onChanged() {
    this.props.onChanged && this.props.onChanged(this.state.current);
  }

  onBlur() {
    this.setState({ show: DatePickerState.Closed });
    this.props.input.onBlur(true);
  }

  onFocus() {
    if (this.state.disabled === false) {
      this.setState({ show: DatePickerState.Date });
      this.props.input.onFocus(true);
    }
  }

  changeDate(e) {
    this.setState({ show: DatePickerState.Time, temp: moment(e) });
  }

  onClickOut(e) {
    if (this.state.show === DatePickerState.Closed) return;

    function ignore(e) {
      return ['rdtDay', 'rdtActive', 'rdtBtn', 'rdt', 'rdtCount'].some(
        (item) => e.target.classList.length !== 0 && e.target.classList.contains(item)
      );
    }
    this.props.input.onBlur(true);
    if (ignore(e)) return;
    this.setState({ show: DatePickerState.Closed });
  }

  changeTime(data) {
    const current = this.state.temp.clone();
    current.hour(data.hour);
    current.minute(data.minute);
    this.props.input.onChange(current.format('YYYY-MM-DDTHH:mm:ssZ'));
    this.props.input.onBlur(true);

    this.setState({ current, temp: null, show: DatePickerState.Closed }, () => this.onChanged());
  }

  render() {
    const now = moment(this.state.current).format('M/D/YYYY h:mm A');

    return (
      <ClickOutHandler onClickOut={(e) => this.onClickOut(e)}>
        <input
          disabled={this.state.disabled}
          onClick={() => this.onFocus()}
          value={now !== 'Invalid date' ? now : ''}
          readOnly
        />
        <Datetime
          onChange={(e) => this.changeDate(e)}
          open={this.state.show === DatePickerState.Date}
          value={moment(this.state.current)}
          timeFormat={false}
          input={false}
          utc={false}
        />
        <TimePicker
          show={this.state.show === DatePickerState.Time}
          hour={this.state.current.hour()}
          minute={this.state.current.minute()}
          onChange={(data) => this.changeTime(data)}
        />
      </ClickOutHandler>
    );
  }
}

class InlineDateAdapter extends DateAdapter {
  constructor(props) {
    super(props);

    this.state = {
      show: DatePickerState.Closed,
      current: moment(props.value),
      temp: null,
      disabled: props.disabled ?? false,
    };
  }

  render() {
    const now = moment(this.state.current).format('M/D/YYYY h:mm A');

    return (
      <ClickOutHandler onClickOut={(e) => this.onClickOut(e)}>
        <input
          type="hidden"
          disabled={this.state.disabled}
          value={now !== 'Invalid date' ? now : ''}
        />

        <>
          {now !== 'Invalid date' ? (
            <>
              <span style={{ textDecoration: 'underline' }}>{now}</span>
              <img
                onClick={() => this.onFocus()}
                style={{
                  height: '15px',
                  width: '15px',
                  marginTop: '-5px',
                  cursor: 'pointer',
                  marginLeft: '10px',
                }}
                src="/img/edit@3x.png"
                alt="Edit"
              />
            </>
          ) : (
            <>
              <span
                onClick={() => this.onFocus()}
                style={{
                  color: 'var(--color-beige)',
                  cursor: 'pointer',
                  fontWeight: 900,
                  fontSize: '14px',
                  textDecoration: 'underline',
                }}
              >
                + Add
              </span>
            </>
          )}
        </>

        <Datetime
          onChange={(e) => this.changeDate(e)}
          open={this.state.show === DatePickerState.Date}
          value={moment(this.state.current)}
          timeFormat={false}
          input={false}
        />

        <TimePicker
          show={this.state.show === DatePickerState.Time}
          hour={this.state.current.hour()}
          minute={this.state.current.minute()}
          onChange={(data) => this.changeTime(data)}
        />
      </ClickOutHandler>
    );
  }
}

const TimePickerContainer = styled.div`
  background-color: white;
  box-shadow: 0 4px 6px 0 hsla(0, 0%, 0%, 0.2);
  position: absolute;
  width: 200px;
  padding: 10px;
  font-family: BrandonGrotesque-Regular;
  z-index: 1;
`;

interface TimePickerProps {
  hour: any;
  minute: any;
  show: boolean;
  onChange: Function;
}

interface TimePickerState {
  hour: number;
  minute: any;
  show: 'hour' | 'minute';
}

class TimePicker extends React.Component<TimePickerProps, TimePickerState> {
  constructor(props) {
    super(props);
    this.state = { hour: props.hour, minute: props.minute, show: 'hour' };
  }

  changeHour(hour) {
    this.setState({ hour, show: 'minute' });
  }

  changeMinute(minute) {
    this.setState({ minute, show: 'hour' });
    this.props.onChange({ hour: this.state.hour, minute });
  }

  render() {
    if (!this.props.show) return null;
    return (
      <ClickOutHandler onClickOut={(e) => this.changeMinute(this.state.minute)}>
        {this.state.show === 'hour' ? (
          <TimePickerContainer>
            <div style={{ textAlign: 'center', fontSize: '17px' }}>AM</div>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <HourPickerItem
                    title={12}
                    onClick={() => this.changeHour(0)}
                    className={`btn ${this.state.hour === 0 ? 'btn-info' : ''}`}
                  />
                  {[1, 2, 3, 4, 5].map((i) => (
                    <HourPickerItem
                      key={i}
                      title={i}
                      onClick={() => this.changeHour(i)}
                      className={`btn ${this.state.hour === i ? 'btn-info' : ''}`}
                    />
                  ))}
                </tr>
                <tr>
                  {[6, 7, 8, 9, 10, 11].map((i) => (
                    <HourPickerItem
                      key={i}
                      title={i}
                      onClick={() => this.changeHour(i)}
                      className={`btn ${this.state.hour === i ? 'btn-info' : ''}`}
                    />
                  ))}
                </tr>
              </tbody>
            </table>
            <div style={{ textAlign: 'center', fontSize: '17px' }}>PM</div>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <HourPickerItem
                    key={12}
                    title={12}
                    onClick={() => this.changeHour(12)}
                    className={`btn ${this.state.hour === 12 ? 'btn-info' : ''}`}
                  />
                  {[1, 2, 3, 4, 5].map((i) => (
                    <HourPickerItem
                      key={i + 12}
                      title={i}
                      onClick={() => this.changeHour(i + 12)}
                      className={`btn ${this.state.hour - 12 === i ? 'btn-info' : ''}`}
                    />
                  ))}
                </tr>
                <tr>
                  {[6, 7, 8, 9, 10, 11].map((i) => (
                    <HourPickerItem
                      key={i + 12}
                      title={i + 12}
                      onClick={() => this.changeHour(i + 12)}
                      className={`btn ${this.state.hour - 12 === i ? 'btn-info' : ''}`}
                    />
                  ))}
                </tr>
              </tbody>
            </table>
            <br />
          </TimePickerContainer>
        ) : null}
        {this.state.show === 'minute' ? (
          <TimePickerContainer>
            <div style={{ textAlign: 'center', fontSize: '17px' }}>
              {this.state.hour < 12 ? 'AM' : 'PM'}
            </div>
            <table style={{ width: '100%' }}>
              <tbody>
                {[
                  ['00', '05', 10, 15],
                  [20, 25, 30, 35],
                  [40, 45, 50, 55],
                ].map((row, index) => (
                  <tr key={'row' + index}>
                    {row.map((i) => (
                      <HourPickerItem
                        key={moment(this.state.hour, 'H').format('h:') + i}
                        wide={true}
                        title={moment(this.state.hour, 'H').format('h:') + i}
                        onClick={() => this.changeMinute(i)}
                        // eslint-disable-next-line
                        className={`btn ${this.state.minute == i ? 'btn-info' : ''}`}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TimePickerContainer>
        ) : null}
      </ClickOutHandler>
    );
  }
}
export { DateAdapter, InlineDateAdapter, TimePicker };
